import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Existing elements
import SliderOneSection from '../elements/home-slider/slider1';
import Modal from '../elements/modal';
import AvailableCitiesModal from '../elements/available-cities-modal';
import TestimonialSection from '../elements/testimonial';
import PartnerShipsSection from '../elements/partnerships';
import brandsData from '../../data/brands_data.json';
import cityTitleMapImport from '../../data/city-title-map.json';
import LatestNewsSection from '../elements/latest-news';
import { getLocalItem, getSessionItem } from '../../utils/storage';
import { startingPriceLabel } from '../../utils/pricing';

// Premium components
import {
  StickyMobileCTA,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FaqAccordion,
  FinalCTA,
  BlogCard,
  Breadcrumbs,
  StructuredData,
  RelatedTopServices,
  ProgrammaticLinkHub,
} from '../elements/premium';
import { readInjectedLinkData, consumeInjectedLinkData, fetchLinkData } from '../../utils/linkHubData';

// App store icons
import googlePlayIconImport from '../../images/GooglePlayIcon.webp';
import appStoreIconImport from '../../images/AppleAppStoreIcon.webp';

// City-page slug → programmatic-page city key (most match 1:1; Bengaluru
// is served at /city/bengaluru but the programmatic pages use 'bangalore').
const CITY_KEY_TO_PROG = { bengaluru: 'bangalore' };

// Hoisted starting-price labels — resolved from pricing-snapshot.json at module load.
const SITE_FROM = startingPriceLabel('site');
const BIKE_FROM = startingPriceLabel('bike');
const CAR_FROM  = startingPriceLabel('car');
const EV_FROM   = startingPriceLabel('ev');

const cityTitleMap = (cityTitleMapImport && cityTitleMapImport.cities) || {};
const tierTemplates = (cityTitleMapImport && cityTitleMapImport.tier_templates) || {};
const areaOverrides = (cityTitleMapImport && cityTitleMapImport.areas) || {};
const googlePlayIcon = typeof googlePlayIconImport === 'object' ? googlePlayIconImport.default : googlePlayIconImport;
const appStoreIcon = typeof appStoreIconImport === 'object' ? appStoreIconImport.default : appStoreIconImport;

// Render a tier template with the {loc} token replaced. Falls back to
// the 'default' template if the requested tier isn't defined.
function renderTier(tierKey, loc) {
  const tpl = tierTemplates[tierKey] || tierTemplates.default;
  if (!tpl) return null;
  return {
    title: (tpl.title_format || '').split('{loc}').join(loc),
    description: (tpl.description_format || '').split('{loc}').join(loc),
    hero_h1: (tpl.hero_h1_format || '').split('{loc}').join(loc),
  };
}

// Service category data
const SERVICE_CATEGORIES = [
  { icon: '🏍️', label: 'Bike Service', desc: 'Regular maintenance, oil change, tune-up', href: '/bike-service-near-me' },
  { icon: '🚗', label: 'Car Service', desc: 'Periodic service, engine care, inspections', href: '/car-service-near-me' },
  { icon: '❄️', label: 'Car AC Repair', desc: 'Car AC gas refill, compressor, cooling fix', href: '/car-ac-repair-near-me' },
  { icon: '🔋', label: 'Battery', desc: 'Jump start, replacement, testing', href: '/car-battery-replacement-near-me' },
  { icon: '🛞', label: 'Tyre & Wheel', desc: 'Puncture, replacement, alignment', href: '/car-tyre-replacement-near-me' },
  { icon: '🔧', label: 'Engine Repair', desc: 'Diagnostics, overhaul, performance', href: '/car-engine-repair-near-me' },
  { icon: '🎨', label: 'Denting & Painting', desc: 'Scratch removal, body work, polish', href: '/car-painting-near-me' },
  { icon: '⚡', label: 'EV Service', desc: `Electric bike & scooter service from ${EV_FROM}`, href: '/electric-bike-service' },
];

// How it works steps
const STEPS = [
  { no: '01', icon: '📱', title: 'Book Online', desc: 'Select your vehicle, choose a service, pick a time slot — under 60 seconds.' },
  { no: '02', icon: '🚐', title: 'Mechanic Arrives', desc: 'Our certified, uniformed mechanic arrives at your doorstep — on time, every time.' },
  { no: '03', icon: '🔧', title: 'Service Done', desc: 'Professional service with genuine parts. Real-time updates and photos shared via app.' },
  { no: '04', icon: '✅', title: 'Warranty Included', desc: 'Digital job card, before/after photos, and a 30-day service warranty — guaranteed.' },
];

// Why choose us items
const WHY_US_ITEMS = [
  { icon: '🏠', title: 'Doorstep Service', desc: 'Expert mechanics come to your home or office — no garage trips needed.' },
  { icon: '🎓', title: 'Certified Mechanics', desc: 'Trained, verified professionals with years of experience across all brands.' },
  { icon: '💰', title: 'Transparent Pricing', desc: 'No hidden charges. See exact pricing before you book — what you see is what you pay.' },
  { icon: '🔒', title: 'Genuine Parts', desc: 'OEM and genuine spare parts only. We never compromise on quality.' },
  { icon: '📸', title: 'Live Updates', desc: 'Before/after photos, real-time progress tracking, and digital job card via our app.' },
  { icon: '🛡️', title: '30-Day Warranty', desc: 'Every service backed by a 30-day warranty on parts and labour.' },
];

// Homepage FAQs
const HOME_FAQS = [
  { q: 'What services does Ride N Repair offer?', a: 'We provide doorstep bike and car services including periodic maintenance, engine repair, AC service, battery replacement, tyre care, denting & painting, and emergency repairs across 32+ cities in India.' },
  { q: 'How do I book a service?', a: 'You can book online through our website or mobile app in under 60 seconds. Select your vehicle, choose a service, pick a time slot, and our certified mechanic arrives at your doorstep.' },
  { q: 'Is doorstep service available in my city?', a: 'We currently serve 32+ cities across India including Delhi NCR, Bengaluru, Mumbai, Pune, Hyderabad, Chennai, Kolkata, Ahmedabad, Jaipur, and many more. Check our Cities section for the full list.' },
  { q: 'What brands do you service?', a: 'We service all major two-wheeler and four-wheeler brands — Honda, Hero, Bajaj, TVS, Royal Enfield, Yamaha, Maruti Suzuki, Hyundai, Tata, Mahindra, Kia, Toyota, BMW, Mercedes, and 40+ more brands.' },
  { q: 'Do you offer a warranty on services?', a: 'Yes, every service comes with a 30-day warranty on parts and labour. We use genuine OEM parts and provide before/after photos plus a digital job card for complete transparency.' },
  { q: 'What are your pricing and payment options?', a: `Our pricing is fully transparent — you see the exact cost before booking with no hidden charges. We accept UPI, cards, net banking, and cash. Bike services start from ${BIKE_FROM} and car services from ${CAR_FROM}.` },
];

class Index extends Component {
  constructor(props) {
    super(props);
    // City link-hub data is SSR-injected (window.__LINK_HUB_DATA__) on first
    // load and fetched on SPA nav — never bundled. Seed from the injection so
    // the first client render matches the server HTML (no hydration mismatch).
    const progKey = props.cityKey ? (CITY_KEY_TO_PROG[props.cityKey] || props.cityKey) : null;
    const injected = progKey ? readInjectedLinkData('city', progKey) : null;
    this.state = { progCityLinks: injected ? injected.data : null };
  }

  componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;

    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }

    if (isMobileApp) {
      history.push('/register');
    }

    this.loadCityLinks(true);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.cityKey !== this.props.cityKey) this.loadCityLinks(false);
  }

  loadCityLinks(isInitial) {
    const { cityKey } = this.props;
    if (!cityKey) { this.setState({ progCityLinks: null }); return; }
    const progKey = CITY_KEY_TO_PROG[cityKey] || cityKey;
    const injected = consumeInjectedLinkData('city', progKey);
    if (injected) { this.setState({ progCityLinks: injected.data }); return; }
    if (isInitial && this.state.progCityLinks) return; // already seeded in constructor
    this.setState({ progCityLinks: null });
    fetchLinkData(`/_data/links/city/${progKey}`)
      .then((p) => this.setState({ progCityLinks: p && p.data ? p.data : null }));
  }

  getLocationText() {
    const { cityKey, areaKey, cityContent } = this.props;
    if (!cityKey) return '';

    const formattedCityKey = cityKey
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    let areaLabel = '';
    if (cityContent[cityKey]?.areas) {
      const areaObj = cityContent[cityKey].areas.find((a) => a.value === areaKey);
      if (areaObj) areaLabel = areaObj.label;
    }

    if (areaLabel) return ` in ${areaLabel}, ${formattedCityKey}`;
    return ` in ${formattedCityKey}`;
  }

  getFormattedCityKey() {
    const { cityKey } = this.props;
    if (!cityKey) return '';
    return cityKey
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  getCityDescription() {
    const { cityKey, cityContent } = this.props;
    if (!cityKey || !cityContent[cityKey]?.description) return '';
    return cityContent[cityKey].description.trim();
  }

  render() {
    const {
      isMobileApp,
      isMobileBrowser,
      cityKey,
      areaKey,
      cityContent,
      user,
      setUser,
      openModal,
      setOpenModal,
      citiesModal,
      setCitiesModal,
      blogsData,
    } = this.props;

    const locationText = this.getLocationText();
    const formattedCity = this.getFormattedCityKey();
    const cityDescription = this.getCityDescription();

    // Dynamic meta
    let pageCanonical = 'https://www.ridenrepair.com/';
    let pageTitle = 'Doorstep Bike & Car Repair Near Me | Ride N Repair';
    let pageDescription = `Expert mechanics at your doorstep in 32+ cities. Bike service from ${BIKE_FROM}, car repair from ${CAR_FROM}. Book in 30 seconds — 30-day warranty.`;

    // Hero H1 override — when present in city-title-map, the visible page H1
    // matches the SERP <title> so the user lands on a page that delivers what
    // the SERP promised. Critical for SERP→landing CTR & dwell.
    let heroOverrideH1 = '';

    if (cityKey) {
      if (areaKey) {
        // Area pages inherit their parent city's tier so the title matches
        // the same query-intent as the parent — e.g. Bangalore (keep-new)
        // areas get "Bike & Car Service in {Area}, Bangalore", Chennai
        // (fix-mechanic-shop) areas get "Mechanic Shop Near Me in {Area},
        // Chennai", etc. Resolution order:
        //   1. Explicit override in city-title-map.json `areas` block
        //   2. Parent city's tier_template applied to "{Area}, {City}"
        //   3. Default template (Mechanic Near Me)
        const areaObj = cityContent[cityKey]?.areas?.find((a) => a.value === areaKey);
        const areaLabel = areaObj?.label || '';
        if (areaLabel) {
          pageCanonical = `https://www.ridenrepair.com/city/${cityKey}/${areaKey}`;
          const explicit = areaOverrides[`${cityKey}/${areaKey}`];
          if (explicit && explicit.title) {
            pageTitle = explicit.title;
            pageDescription = explicit.description;
            heroOverrideH1 = explicit.hero_h1 || '';
          } else {
            const parentCfg = cityTitleMap[cityKey];
            const parentTier = (parentCfg && parentCfg.tier) || 'default';
            // For fix-branded cities (e.g. Gurgaon), parent title may use
            // a different city label than formattedCity (Gurugram → Gurgaon).
            // Extract the parent's display label from its hero_h1 prefix.
            let parentCityDisplay = formattedCity;
            if (parentCfg && parentCfg.hero_h1) {
              const m = parentCfg.hero_h1.match(/(?:in|N Repair) ([A-Za-z][A-Za-z .'-]+?)(?: —| -|$)/);
              if (m && m[1]) parentCityDisplay = m[1].trim();
            }
            const loc = `${areaLabel}, ${parentCityDisplay}`;
            const rendered = renderTier(parentTier, loc);
            if (rendered) {
              pageTitle = rendered.title;
              pageDescription = rendered.description;
              heroOverrideH1 = rendered.hero_h1;
            } else {
              pageTitle = `Mechanic Near Me in ${loc} — Doorstep Bike & Car Service from ${SITE_FROM} | Ride N Repair`;
              pageDescription = `Find a mechanic near you in ${loc}. Doorstep bike & car repair from ${SITE_FROM}. Mechanics arrive in 15 min, 30-day warranty.`;
              heroOverrideH1 = `Mechanic Near Me in ${loc} — Doorstep Bike & Car Service`;
            }
          }
        }
      } else {
        pageCanonical = `https://www.ridenrepair.com/city/${cityKey}`;
        // Per-city tiered titles (see src/data/city-title-map.json).
        // Cities with explicit map entries get data-driven titles based on
        // their lead-impression query and PRE/POST booking trajectory.
        // All other cities default to the lead-intent "Mechanic Near Me"
        // pattern which historically converts.
        const cityCfg = cityTitleMap[cityKey];
        if (cityCfg && cityCfg.title) {
          pageTitle = cityCfg.title;
          pageDescription = cityCfg.description;
          heroOverrideH1 = cityCfg.hero_h1 || '';
        } else {
          const rendered = renderTier('default', formattedCity);
          if (rendered) {
            pageTitle = rendered.title;
            pageDescription = rendered.description;
            heroOverrideH1 = rendered.hero_h1;
          } else {
            pageTitle = `Mechanic Near Me in ${formattedCity} — Doorstep Bike & Car Service from ${SITE_FROM} | Ride N Repair`;
            pageDescription = `Find a mechanic near you in ${formattedCity}. Doorstep bike & car repair from ${SITE_FROM}. Mechanics arrive in 15 min, 30-day warranty, 2,00,000+ customers.`;
            heroOverrideH1 = `Mechanic Near Me in ${formattedCity}`;
          }
        }
      }
    }
    this._heroOverrideH1 = heroOverrideH1;

    // Structured data schemas
    const schemas = [
      {
        '@type': 'Organization',
        name: 'Ride N Repair',
        url: 'https://www.ridenrepair.com',
        logo: 'https://www.ridenrepair.com/images/logo.webp',
        description: 'Doorstep bike and car service & repair across 32+ cities in India.',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+91 120 361 5050',
          contactType: 'customer service',
          areaServed: 'IN',
        },
        sameAs: [
          'https://play.google.com/store/apps/details?id=com.ridenrepair.app',
          'https://apps.apple.com/us/app/ridenrepair/id6446174085',
        ],
      },
      {
        '@type': 'WebSite',
        name: 'Ride N Repair',
        url: 'https://www.ridenrepair.com',
      },
    ];

    if (cityKey) {
      // Pull optional city-specific LocalBusiness enrichment (address region/postal,
      // geo coordinates, service-area radius) from city_content.json when present.
      // Cities without this block fall back to the country/city-only address used
      // historically. Bengaluru ships these fields first (April 2026); other cities
      // can opt in over time.
      const cityLB = (cityContent[cityKey] && cityContent[cityKey].localBusiness) || null;
      const localBusinessSchema = {
        '@type': 'LocalBusiness',
        '@id': pageCanonical,
        name: `Ride N Repair — ${formattedCity}`,
        description: `Doorstep bike and car service in ${formattedCity}. Certified mechanics, genuine parts, 30-day warranty.`,
        url: pageCanonical,
        telephone: '+91 120 361 5050',
        image: 'https://www.ridenrepair.com/images/logo.webp',
        address: {
          '@type': 'PostalAddress',
          addressLocality: (cityLB && cityLB.addressLocality) || formattedCity,
          ...(cityLB && cityLB.addressRegion ? { addressRegion: cityLB.addressRegion } : {}),
          ...(cityLB && cityLB.postalCode ? { postalCode: cityLB.postalCode } : {}),
          addressCountry: 'IN',
        },
        areaServed: { '@type': 'City', name: formattedCity },
        priceRange: `${SITE_FROM} - ₹25,000`,
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '07:00',
          closes: '21:00',
        },
      };
      if (cityLB && cityLB.geo && cityLB.geo.latitude && cityLB.geo.longitude) {
        localBusinessSchema.geo = {
          '@type': 'GeoCoordinates',
          latitude: cityLB.geo.latitude,
          longitude: cityLB.geo.longitude,
        };
        if (cityLB.serviceArea && cityLB.serviceArea.geoRadius) {
          localBusinessSchema.serviceArea = {
            '@type': 'GeoCircle',
            geoMidpoint: {
              '@type': 'GeoCoordinates',
              latitude: cityLB.geo.latitude,
              longitude: cityLB.geo.longitude,
            },
            geoRadius: cityLB.serviceArea.geoRadius,
          };
        }
      }
      schemas.push(localBusinessSchema);
    }

    // Location-aware FAQs. When city_content.json[cityKey].faqs is present,
    // its city-specific FAQ entries lead the list (high snippet/CTR value),
    // followed by the standard location-aware FAQ and the homepage FAQs.
    const cityFaqs = (cityKey && Array.isArray(cityContent[cityKey]?.faqs)) ? cityContent[cityKey].faqs : null;
    const faqs = cityKey
      ? [
          ...(cityFaqs || []),
          { q: `Is doorstep service available in ${formattedCity}?`, a: `Yes! Ride N Repair provides doorstep bike and car repair services across ${formattedCity}. Our certified mechanics will come to your home or office for all types of vehicle maintenance and repairs.` },
          ...HOME_FAQS.slice(0, 5),
        ]
      : HOME_FAQS;

    // Blog data for display
    const displayBlogs = (blogsData || []).slice(0, 3);

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <link rel="canonical" href={pageCanonical} />
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="keywords" content="bike service near me, car service near me, doorstep bike repair, doorstep car repair, two wheeler service, four wheeler service, bike mechanic near me, car mechanic near me, Ride N Repair" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={pageCanonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        {/* Phone Number Modal */}
        {!isMobileApp && openModal && (
          <Modal
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            openModal={openModal}
            setOpenModal={setOpenModal}
            user={user}
            setUser={setUser}
          />
        )}

        {/* Available Cities Modal */}
        {citiesModal && (
          <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />
        )}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        {/* BREADCRUMBS — JSON-LD schema for city/area pages */}
        {cityKey && (
          <Breadcrumbs items={[
            { label: 'Home', href: '/' },
            ...(areaKey
              ? [
                  { label: formattedCity, href: `/city/${cityKey}` },
                  { label: (cityContent[cityKey]?.areas?.find(a => a.value === areaKey)?.label) || areaKey },
                ]
              : [{ label: formattedCity }]
            ),
          ]} />
        )}

        {/* HERO — existing slider with car animation. Pass tier-mapped H1
            so /city/* pages display the SERP-promised heading on landing. */}
        <SliderOneSection
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          cityKey={cityKey}
          setCitiesModal={setCitiesModal}
          setOpenModal={setOpenModal}
          areaKey={areaKey}
          cityContent={cityContent}
          heading={this._heroOverrideH1 || ''}
        />

        {/* STICKY MOBILE CTA */}
        <StickyMobileCTA
          title="Bike & Car Service"
          priceHint={`Starting ${SITE_FROM} · 30-Day Warranty`}
          btnLabel="Book Now"
          btnHref="/service"
        />

        {/* TRUST BAR removed — same signals (200K customers, 4.8★, 32+
            cities, 30-day warranty) already render inside the hero via
            .rr-hero-trust on slider1.jsx. Avoid duplicating below the hero. */}

        {/* SERVICE CATEGORIES */}
        <SectionWrapper kicker="Our Services" heading={`What We Fix${locationText}`}>
          <div className="rr-service-grid">
            {SERVICE_CATEGORIES.map((cat) => (
              <Link key={cat.label} to={cat.href} className="rr-service-card">
                <span className="rr-service-card__icon">{cat.icon}</span>
                <span className="rr-service-card__label">{cat.label}</span>
                <span className="rr-service-card__desc">{cat.desc}</span>
              </Link>
            ))}
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/service" className="rr-cta-primary">Browse All Services</Link>
          </div>
        </SectionWrapper>

        {/* INTRODUCTION — city-aware content */}
        <SectionWrapper variant="gray" kicker="Overview" heading={`Bike & Car Service at Your Doorstep${cityKey ? ` in ${formattedCity}` : ''}`}>
          <div className="rr-keyword__intro">
            <p>
              Looking for reliable bike and car repair services{locationText}? <strong>Ride N Repair</strong> brings
              professional mechanics straight to your doorstep — saving you time, effort, and travel. From <strong>bike
              servicing</strong> to <strong>car engine repairs</strong>, we offer complete solutions using genuine parts
              and skilled technicians, right where you are.
            </p>
            <p>
              Whether you need a <strong>bike service</strong>, <strong>car AC repair</strong>, <strong>battery
              replacement</strong>, or <strong>emergency breakdown assistance</strong>{locationText}, our certified
              mechanics handle everything with professional care. We pride ourselves on offering transparent pricing,
              genuine parts, and quality workmanship — backed by a 30-day service warranty.
            </p>
            {cityDescription && (
              <>
                <h3 style={{ marginTop: 24 }}>About {formattedCity}</h3>
                <p>{cityDescription}</p>
              </>
            )}
            {/* City-specific local signals (HTML block from city_content.json).
                Renders only when present — Bengaluru ships first; safe no-op for
                cities that haven't been enriched yet. */}
            {cityKey && cityContent[cityKey]?.local_signals_html && (
              <div
                className="rr-keyword__local-signals"
                dangerouslySetInnerHTML={{ __html: cityContent[cityKey].local_signals_html }}
              />
            )}
            {/* Neighborhoods served — internal links to /city/{cityKey}/{slug}.
                Each slug is matched against the cityContent.areas list before
                linking; if not present we render plain text so we never ship a
                broken link. */}
            {cityKey && Array.isArray(cityContent[cityKey]?.neighborhoods_featured) && cityContent[cityKey].neighborhoods_featured.length > 0 && (
              <>
                <h3 style={{ marginTop: 24 }}>Neighborhoods we serve in {formattedCity}</h3>
                <ul className="rr-neighborhood-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', listStyle: 'none', padding: 0, margin: 0 }}>
                  {cityContent[cityKey].neighborhoods_featured.map((n) => {
                    const areaMatch = (cityContent[cityKey].areas || []).find((a) => a.value === n.slug);
                    if (areaMatch) {
                      return (
                        <li key={n.slug}>
                          <Link to={`/city/${cityKey}/${n.slug}`}>{n.label}</Link>
                        </li>
                      );
                    }
                    return <li key={n.slug || n.label}>{n.label}</li>;
                  })}
                </ul>
              </>
            )}
            {/* Top brands serviced in this city. Static list — for SEO entity
                relevance + reader scan. Only renders when present. */}
            {cityKey && (Array.isArray(cityContent[cityKey]?.top_car_brands) || Array.isArray(cityContent[cityKey]?.top_bike_brands)) && (
              <p style={{ marginTop: 16 }}>
                {Array.isArray(cityContent[cityKey].top_car_brands) && cityContent[cityKey].top_car_brands.length > 0 && (
                  <>Top car brands serviced in {formattedCity}: <strong>{cityContent[cityKey].top_car_brands.join(', ')}</strong>. </>
                )}
                {Array.isArray(cityContent[cityKey].top_bike_brands) && cityContent[cityKey].top_bike_brands.length > 0 && (
                  <>Top bike brands: <strong>{cityContent[cityKey].top_bike_brands.join(', ')}</strong>.</>
                )}
              </p>
            )}
          </div>
          <div className="rr-callout">
            <p className="rr-callout__label">Mechanic on Call</p>
            <a className="rr-callout__phone" href="tel:+911203615050">+91 120 361 5050</a>
          </div>
        </SectionWrapper>

        {/* APP DOWNLOAD — only on web, not logged in */}
        {!isMobileApp && !user.is_logged && (
          <SectionWrapper kicker="Download" heading="Get the Ride N Repair App">
            <p className="rr-sub">Book services, track repairs in real-time, and earn referral rewards — all from your phone.</p>
            <div className="rr-store-row" style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24 }}>
              <a href="https://play.google.com/store/apps/details?id=com.ridenrepair.app" className="rr-store-badge">
                <img src={googlePlayIcon} alt="Google Play Store" width="160" height="48" loading="lazy" />
              </a>
              <a href="https://apps.apple.com/us/app/ridenrepair/id6446174085" className="rr-store-badge">
                <img src={appStoreIcon} alt="Apple App Store" width="160" height="48" loading="lazy" />
              </a>
            </div>
          </SectionWrapper>
        )}

        {/* REFERRAL */}
        <SectionWrapper variant="gray" kicker="Rewards" heading="Refer & Earn ₹500">
          <p className="rr-sub">Share Ride N Repair with friends — they get ₹500 off their first service, and you earn ₹500 when they complete it.</p>
          <div className="rr-steps-grid" style={{ marginTop: 32 }}>
            <div className="rr-step-card rr-step-card--light">
              <div className="rr-step-card__no">01</div>
              <div className="rr-step-card__title">Share Your Code</div>
              <div className="rr-step-card__desc">Share your unique referral code from your profile with friends.</div>
            </div>
            <div className="rr-step-card rr-step-card--light">
              <div className="rr-step-card__no">02</div>
              <div className="rr-step-card__title">Friend Gets ₹500 Off</div>
              <div className="rr-step-card__desc">They receive ₹500 off their first doorstep service booking.</div>
            </div>
            <div className="rr-step-card rr-step-card--light">
              <div className="rr-step-card__no">03</div>
              <div className="rr-step-card__title">You Earn ₹500</div>
              <div className="rr-step-card__desc">Once they complete their service, ₹500 is automatically credited to you.</div>
            </div>
          </div>
        </SectionWrapper>

        {/* HOW IT WORKS */}
        <SectionWrapper variant="dark" kicker="Simple Process" heading="How Ride N Repair Works">
          <StepCards steps={STEPS} variant="dark" />
          <div className="rr-text-center rr-mt-32">
            <Link to={isMobileApp ? '/register' : '/service'} className="rr-cta-primary">Book Now</Link>
          </div>
        </SectionWrapper>

        {/* WHY CHOOSE US */}
        <SectionWrapper variant="gray" kicker="Our Promise" heading="Why 2,00,000+ Customers Choose Ride N Repair">
          <WhyUsGrid items={WHY_US_ITEMS} />
        </SectionWrapper>

        {/* TESTIMONIALS — keep existing paginated component */}
        <TestimonialSection isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} />

        {/* FAQs — premium accordion */}
        <SectionWrapper narrow kicker="Common Questions" heading={`Frequently Asked Questions${locationText}`}>
          <FaqAccordion faqs={faqs} />
        </SectionWrapper>

        {/* BLOGS — premium cards */}
        {displayBlogs.length > 0 && (
          <SectionWrapper variant="gray" kicker="Insights" heading="Latest from Our Blog">
            <div className="rr-card-grid--3col">
              {displayBlogs.map((blog, i) => (
                <BlogCard
                  key={i}
                  slug={blog.slug}
                  title={blog.title}
                  excerpt={blog.summary || blog.blogText}
                  image={blog.thumb}
                  category={blog.category}
                  date={blog.date}
                  readTime={blog.readTime}
                />
              ))}
            </div>
            <div className="rr-text-center rr-mt-32">
              <Link to="/blogs" className="rr-cta-dark-outline">View All Blogs</Link>
            </div>
          </SectionWrapper>
        )}

        {/* LATEST NEWS / PRESS */}
        <LatestNewsSection />

        {/* TOP BRANDS */}
        <SectionWrapper kicker="Trusted Brands" heading={`Brands We Service${locationText}`}>
          <div className="rr-brand-grid">
            {(() => {
              const allBrands = [...(brandsData.bike || []), ...(brandsData.car || [])];
              const seen = new Set();
              return allBrands
                .filter(b => { if (seen.has(b.slug)) return false; seen.add(b.slug); return true; })
                .slice(0, 12)
                .map(b => {
                  let logoSrc = null;
                  try { const l = require(`../../images/brand_logos/${b.logo}`); logoSrc = typeof l === 'object' ? l.default : l; } catch (e) {}
                  return (
                    <Link key={b.slug} to={`/brands/${b.slug}`} className="rr-brand-card">
                      {logoSrc ? <img src={logoSrc} alt={b.label} className="rr-brand-card__logo" loading="lazy" /> : <div className="rr-brand-card__logo" style={{ fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{b.label.charAt(0)}</div>}
                      <span className="rr-brand-card__label">{b.label}</span>
                      <span className="rr-brand-card__count">{b.models.length} models</span>
                    </Link>
                  );
                });
            })()}
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/brands" className="rr-cta-dark-outline">View All Brands</Link>
          </div>
        </SectionWrapper>

        {/* RELATED HIGH-LTV SERVICES — booking-driven internal links.
            Only on city/area pages (not homepage) to avoid duplication
            with the brand grid above. */}
        {cityKey && (
          <RelatedTopServices
            variant="city"
            kicker="Popular With Customers"
            heading={`Popular Doorstep Service & Repairs${locationText}`}
          />
        )}

        {/* DOORSTEP SERVICE LINK HUB — links into the brand×service leaf pages
            for this city (de-orphans the programmatic pages from a ranking hub). */}
        {cityKey && this.state.progCityLinks && this.state.progCityLinks.length > 0 && (
          <ProgrammaticLinkHub
            heading={`Doorstep Service for Your Brand in ${formattedCity}`}
            subheading={`Choose your brand and book service, repair or AC care at your doorstep in ${formattedCity}.`}
            links={this.state.progCityLinks}
          />
        )}

        {/* CITIES & BRANDS NETWORK — premium pill hub (all links in SSR DOM;
            large groups collapse behind a Show-all toggle, never a link wall). */}
        <PartnerShipsSection
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          cityKey={cityKey}
          areaKey={areaKey}
          cityContent={cityContent}
        />

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Need Vehicle Service{locationText}?<br />Book Now — Get It Done Today.</>}
          subtitle={`Doorstep service · Certified mechanics · Starting ${SITE_FROM} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default Index;
